import { Injectable, inject } from "@angular/core";
import { Auth, user } from "@angular/fire/auth";
import {
	Firestore,
	doc,
	getDoc,
	collection,
	query,
	where,
	getDocs,
} from "@angular/fire/firestore";
import { Observable, from, of, switchMap, map, catchError } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { UserRoles, UserWithRoles } from "../models/user-role.model";

@Injectable({
	providedIn: "root",
})
export class RoleService {
	private auth: Auth = inject(Auth);
	private firestore: Firestore = inject(Firestore);
	private user$ = user(this.auth).pipe(shareReplay(1));

	/**
	 * Get the current authenticated user with their aggregated role set.
	 */
	getCurrentUserWithRoles(): Observable<UserWithRoles | null> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of(null);
				return this.getUserRoles(user.uid).pipe(
					map(
						(roles) =>
							({
								uid: user.uid,
								email: user.email || "",
								displayName: user.displayName || "",
								photoURL: user.photoURL || null,
								roles,
							}) as UserWithRoles,
					),
				);
			}),
		);
	}

	/**
	 * Get the aggregated role set for a specific user.
	 *
	 * ZAN-291: roles are no longer stored on users/{uid}. The source of truth is
	 * the canonical membership docs:
	 *   - global_admin  → existence of globalAdmins/{uid}
	 *   - org roles     → organizationMembers (user_id == uid).roles
	 *   - partner roles → partnerUsers (user_id == uid).roles
	 * The aggregate is what authGuard uses to decide the "only assessee →
	 * redirect to members app" case.
	 */
	getUserRoles(userId: string): Observable<UserRoles> {
		return from(this.aggregateUserRoles(userId)).pipe(
			catchError((error) => {
				console.error("Error getting user roles:", error);
				return of({} as UserRoles);
			}),
		);
	}

	private async aggregateUserRoles(userId: string): Promise<UserRoles> {
		const roles: Record<string, boolean> = {};

		// globalAdmins is admin-only read per the rules — a non-admin self-check
		// gets PERMISSION_DENIED. Treat denial as "not a global admin" so the
		// aggregation still returns the user's org/partner roles.
		try {
			const globalAdminSnap = await getDoc(doc(this.firestore, `globalAdmins/${userId}`));
			if (globalAdminSnap.exists()) {
				roles["global_admin"] = true;
			}
		} catch {
			// not a global admin
		}

		const collectRoles = (snapshot: { forEach: (cb: (d: any) => void) => void }): void => {
			snapshot.forEach((memberDoc: any) => {
				const memberRoles = memberDoc.data()["roles"];
				if (!memberRoles) return;
				for (const key of Object.keys(memberRoles)) {
					if (memberRoles[key] === true) {
						roles[key] = true;
					}
				}
			});
		};

		const [orgSnap, partnerSnap] = await Promise.all([
			getDocs(
				query(
					collection(this.firestore, "organizationMembers"),
					where("user_id", "==", userId),
					where("active", "==", true),
				),
			),
			getDocs(
				query(
					collection(this.firestore, "partnerUsers"),
					where("user_id", "==", userId),
					where("active", "==", true),
				),
			),
		]);
		collectRoles(orgSnap);
		collectRoles(partnerSnap);

		return roles as UserRoles;
	}
}
